'use client';

import { useCallback, useMemo } from 'react';
import { useCurrency } from '../../_components/CurrencyProvider';

/**
 * Glyphs `Intl` has no narrowSymbol for — it hands back the ISO code instead
 * (checked: AED, BHD, KWD, OMR, QAR, SAR). Everything else Intl covers.
 */
const FALLBACK_GLYPHS: Record<string, string> = {
  AED: 'د.إ',
  BHD: 'د.ب',
  KWD: 'د.ك',
  OMR: 'ر.ع.',
  QAR: 'ر.ق',
  SAR: 'ر.س',
};

/**
 * A symbol for `code` derived locally, with no network call.
 *
 * Only used when the provider has not got a real glyph yet. `CurrencyProvider`
 * takes its symbol from the CMS `/currency/list` map, and the SSR seed
 * (`layout.tsx` `fetchCurrencySeed`) falls back to `symbol: <the ISO code>`
 * whenever that call is slow, fails, or `COMMON_SERVICE_URL` is unset — which
 * is why a price rendered "GBP 49.00" for the moment after a currency change
 * before flipping to "£ 49.00".
 *
 * Same derivation Hotels already uses for its price-slider glyph
 * (`HotelsFilters`: `Intl` narrowSymbol off `currencyCode`), plus the handful
 * of currencies Intl misses.
 */
function derivedGlyph(code: string): string {
  const upper = (code || '').toUpperCase();
  if (!upper) return '';
  if (FALLBACK_GLYPHS[upper]) return FALLBACK_GLYPHS[upper];
  try {
    const glyph = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: upper,
      currencyDisplay: 'narrowSymbol',
    })
      .formatToParts(0)
      .find((p) => p.type === 'currency')?.value;
    if (glyph) return glyph;
  } catch {
    // Unknown ISO code — nothing better than the code itself.
  }
  return upper;
}

/**
 * Attractions money, on the SAME flow Hotels and Shows use.
 *
 * The rule both of those follow — and the reason neither ever flashes a bare
 * "USD"/"EUR" — is **convert first, then format in the user's own currency**:
 *
 *   formatPrice(convertPrice(amount, supplierCcy), currencyCode)
 *
 * `formatPrice` only reaches for an ISO code when it is handed a FOREIGN
 * currency it has no rate for (`CurrencyProvider.formatPrice` → `symbolFor`).
 * Passing the supplier's currency straight in is exactly that case, so during
 * the moment before rates resolve — and for any currency the rate table misses
 * — the price rendered as "USD 49.00". Hotels never hits it because
 * `RoomGroup`/`RecommendedPrice` convert first and then format with
 * `currencyCode`, which takes the same-currency branch and uses the live
 * `currencySymbol`. This hook is that pattern, in one place.
 *
 * It also exposes the base↔user pair the Shows price slider uses
 * (`EventsFilters`' `gbpToUser`/`userToGbp`), so the Price Range can display in
 * the selected currency while still sending the backend the catalogue-currency
 * numbers its stored prices are comparable against.
 */
export function useAttractionMoney() {
  const { formatPrice, formatInCurrency, convertPrice, currencyCode, currencySymbol, exchangeRates } = useCurrency();

  /**
   * The provider hands back the ISO code as the "symbol" until the CMS glyph
   * map loads. Detect exactly that case and derive a glyph locally; otherwise
   * use the CMS glyph untouched, so the authoritative symbol always wins.
   */
  const symbolUnresolved = currencySymbol.toUpperCase() === currencyCode.toUpperCase();
  const symbol = useMemo(
    () => (symbolUnresolved ? derivedGlyph(currencyCode) : currencySymbol),
    [symbolUnresolved, currencyCode, currencySymbol],
  );

  /**
   * A price for display, always with the selected currency's symbol.
   *
   * The resolved branch defers to `formatPrice`, so the normal path is byte-for-byte
   * what Hotels renders. The fallback branch reproduces that function's own
   * same-currency output — `` `${symbol} ${amount.toFixed(2)}` `` — with the derived
   * glyph swapped in, so both branches produce identical formatting.
   */
  const money = useCallback(
    (amount: number, fromCurrency: string | null | undefined): string => {
      const converted = convertPrice(amount, fromCurrency || currencyCode);
      return symbolUnresolved
        ? `${symbol} ${converted.toFixed(2)}`
        : formatPrice(converted, currencyCode);
    },
    [formatPrice, convertPrice, currencyCode, symbol, symbolUnresolved],
  );

  /**
   * An amount that is ALREADY in `currency` — a charge that was taken —
   * formatted without converting, so it reads exactly as the card statement
   * does even if the navbar currency has since changed. Same
   * `<symbol> <amount>` shape as `money`, with the same derived-glyph
   * fallback while the CMS symbol map is loading.
   */
  const exact = useCallback(
    (amount: number, currency: string): string => {
      const code = (currency || currencyCode).toUpperCase();
      const formatted = formatInCurrency(amount, code);
      return formatted.startsWith(`${code} `) ? `${derivedGlyph(code)} ${amount.toFixed(2)}` : formatted;
    },
    [formatInCurrency, currencyCode],
  );

  /** Catalogue currency → the currency the visitor picked (for display). */
  const toUserAmount = useCallback(
    (amount: number, baseCurrency: string): number => convertPrice(amount, baseCurrency),
    [convertPrice],
  );

  /**
   * The reverse, for values going back to the API. `exchangeRates[base]` is how
   * many base units equal 1 unit of the user's currency — the same maths
   * `EventsFilters.userToGbp` does, generalised to any catalogue currency.
   * There is no self-rate when base IS the user's currency, hence the no-op.
   */
  const toBaseAmount = useCallback(
    (amount: number, baseCurrency: string): number => {
      const base = (baseCurrency || '').toUpperCase();
      if (!base || base === currencyCode.toUpperCase()) return amount;
      const rate = exchangeRates[base] || exchangeRates[baseCurrency];
      return rate && rate > 0 ? Math.round(amount * rate * 100) / 100 : amount;
    },
    [currencyCode, exchangeRates],
  );

  // `currencySymbol` is the RESOLVED glyph — the Price Range pills and the
  // filter chips label with it, so they never show a code either.
  return { money, exact, toUserAmount, toBaseAmount, currencySymbol: symbol, currencyCode };
}

/**
 * A price rendered in the currency the visitor picked in the navbar.
 *
 * This wrapper exists because the detail page is a server component and the
 * currency context is client-side: only this span is a client island, the rest
 * of the page stays SSR.
 */
export function Money({ amount, currency }: { amount: number; currency: string }) {
  const { money } = useAttractionMoney();
  return <>{money(amount, currency)}</>;
}
